/**
 * @param {HTMLElement} footer
 */
function initBackToTop(footer) {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  footer.querySelectorAll('.footer__back-to-top').forEach((control) => {
    if (!(control instanceof HTMLButtonElement) || control.dataset.backToTopBound === 'true') {
      return;
    }

    control.dataset.backToTopBound = 'true';

    control.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
      });

      const topTarget = document.getElementById('top');
      if (topTarget instanceof HTMLElement) {
        topTarget.focus({ preventScroll: true });
      }
    });
  });
}

/**
 * @param {Document | Element} [root=document]
 */
export function initFooter(root = document) {
  const footer = root.querySelector('.footer');
  const nonDesktopQuery = window.matchMedia('(width < 1440px)');

  if (!(footer instanceof HTMLElement)) {
    return;
  }

  initBackToTop(footer);

  if (footer.dataset.footerDrawersBound === 'true') {
    return;
  }

  footer.dataset.footerDrawersBound = 'true';

  const entries = Array.from(footer.querySelectorAll('[data-footer-section]'))
    .map((section) => {
      if (!(section instanceof HTMLElement)) {
        return null;
      }

      const trigger = section.querySelector('[data-footer-heading-link]');
      const panel = section.querySelector('[data-footer-drawer-panel]');

      if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
        return null;
      }

      return { section, trigger, panel };
    })
    .filter(Boolean);

  function getPanelFocusableElements(panel) {
    if (!(panel instanceof HTMLElement)) {
      return [];
    }

    return Array.from(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      if (!(element instanceof HTMLElement)) {
        return false;
      }

      if (element.closest('[aria-hidden="true"]')) {
        return false;
      }

      return element.getClientRects().length > 0;
    });
  }

  function getLastSubmenuLink(panel) {
    if (!(panel instanceof HTMLElement)) {
      return null;
    }

    const submenuLinks = Array.from(panel.querySelectorAll('.footer__submenu-list .list-item--footer-submenu')).filter(
      (element) => element instanceof HTMLElement
    );

    return submenuLinks.at(-1) ?? null;
  }

  function getNextFocusableAfterSection(section) {
    const footerFocusableElements = getPanelFocusableElements(footer);

    return (
      footerFocusableElements.find((element) => {
        if (!(element instanceof HTMLElement) || section.contains(element)) {
          return false;
        }

        return Boolean(section.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
      }) ?? null
    );
  }

  function setOpen(entry, open) {
    entry.section.classList.toggle('footer__column--open', open);
    entry.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    entry.panel.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  function closeAll(except = null) {
    entries.forEach((entry) => {
      if (entry !== except) {
        setOpen(entry, false);
      }
    });
  }

  function syncMode() {
    entries.forEach((entry) => {
      if (nonDesktopQuery.matches) {
        setOpen(entry, entry.section.classList.contains('footer__column--open'));
        return;
      }

      entry.section.classList.remove('footer__column--open');
      entry.trigger.setAttribute('aria-expanded', 'false');
      entry.panel.setAttribute('aria-hidden', 'false');
    });
  }

  entries.forEach((entry) => {
    entry.trigger.addEventListener('click', (event) => {
      if (!nonDesktopQuery.matches) {
        return;
      }

      event.preventDefault();
      const shouldOpen = !entry.section.classList.contains('footer__column--open');
      closeAll(shouldOpen ? entry : null);
      setOpen(entry, shouldOpen);
    });

    entry.panel.addEventListener('keydown', (event) => {
      if (!nonDesktopQuery.matches || event.key !== 'Tab' || event.shiftKey) {
        return;
      }

      const lastSubmenuLink = getLastSubmenuLink(entry.panel);

      if (!(lastSubmenuLink instanceof HTMLElement) || document.activeElement !== lastSubmenuLink) {
        return;
      }

      const nextFocusableElement = getNextFocusableAfterSection(entry.section);
      event.preventDefault();
      setOpen(entry, false);
      nextFocusableElement?.focus();
    });
  });

  nonDesktopQuery.addEventListener('change', syncMode);
  syncMode();
}

initFooter();
